import { FormItem, Select as FaveSelect } from 'fave-ui';
import { getIn, useFormikContext } from 'formik';
import { TreeSelect } from 'antd';
import { DefaultOptionType } from 'antd/lib/select';
import { CSSProperties, useEffect } from 'react';
import styles from './style.module.css';

type SelectOptionsType = {
  label: string;
  key: string;
  value: string;
};

type SelectProps<T> = {
  name: keyof T;
  label: string;
  placeholder?: string;
  defaultValue?: string;
  showArrow?: boolean;
  allowClear?: boolean;
  bordered?: boolean;
  options?: SelectOptionsType[];
  className?: string;
  isError?: boolean;
  help?: string;
  disabled?: boolean;
  withCheckbox?: boolean;
  treeDefaultExpandAll?: boolean;
  style?: CSSProperties;
  mode?: 'multiple' | 'tags' | undefined;
  isLoadingSelect?: boolean;
  onSelect?: (event: string) => void;
  onSearch?: (value: string) => void;
  onBlur?: () => void;
  filterOption?:
    | boolean
    | ((input: string, option?: DefaultOptionType) => boolean);
  showSearch?: boolean;
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
  dropdownClassName?: string;
  listHeight?: number;
  listItemHeight?: number;
  virtual?: boolean;
};

export const Select = <T extends {}>({
  name,
  label,
  placeholder,
  options,
  className,
  isError,
  help,
  withCheckbox,
  showArrow,
  treeDefaultExpandAll,
  style,
  mode,
  onSelect,
  onSearch,
  onBlur,
  isLoadingSelect,
  filterOption,
  ...otherProps
}: SelectProps<T>) => {
  const { errors, touched, values, setFieldValue, setFieldTouched } =
    useFormikContext<T>();

  const fieldName = name as string;
  const isArrayField = fieldName.includes('.');

  const value = isArrayField
    ? getIn(values, fieldName)
    : values[fieldName as keyof typeof values];
  const error = isArrayField
    ? getIn(errors, fieldName)
    : (errors[fieldName as keyof typeof values] as unknown as string);
  const isTouched = isArrayField
    ? getIn(touched, fieldName)
    : (touched[name] as unknown as string);

  const helpText =
    isTouched &&
    (error !== undefined || (isError !== undefined && help !== undefined))
      ? help && isError
        ? help
        : error
      : '';

  // trigger validate on value change
  // does not work when implemented inside onChange and onDeselect
  useEffect(() => {
    if (value) setFieldTouched(name as string, true, true);
  }, [value]);

  return (
    <FormItem
      label={label}
      validateStatus={error !== undefined && isTouched ? 'error' : 'success'}
      help={helpText}
      className={styles.formItem}
    >
      {!withCheckbox ? (
        <FaveSelect
          onBlur={() => {
            onBlur && onBlur();
            setFieldTouched(String(name), true);
          }}
          placeholder={placeholder}
          options={options}
          onChange={e => {
            onSelect && onSelect(e);
            setFieldValue(name as string, e);
          }}
          onSearch={onSearch}
          className={className}
          value={value}
          mode={mode}
          loading={isLoadingSelect}
          filterOption={filterOption}
          {...otherProps}
        />
      ) : (
        <TreeSelect
          onBlur={() => setFieldTouched(String(name), true)}
          allowClear={true}
          placeholder={placeholder}
          treeCheckable={true}
          style={style}
          className={className}
          showArrow={showArrow}
          treeDefaultExpandAll={treeDefaultExpandAll}
          showCheckedStrategy={TreeSelect.SHOW_CHILD}
          onChange={e => {
            setFieldValue(name as string, e);
          }}
          value={value}
          treeData={
            options?.length
              ? [
                  {
                    title:
                      (values[name] as unknown as [])?.length > 0 ? (
                        <span>Unselect all</span>
                      ) : (
                        <span>Select all</span>
                      ),
                    value: 'all',
                    children: options,
                  },
                ]
              : []
          }
        />
      )}
    </FormItem>
  );
};
